import { default as React, useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { connect } from "react-redux";
import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { AutosizeInput, Badge, Button, ButtonLabel, Type } from "@similarweb/ui-components";

import { groupCreatedUpdated, toggleCreateUpdateSegmentGroupModal } from "app/actions/segmentsModuleActions";
import { StyledItemIcon } from "pro-features/components/core cells/src/CoreRecentCell/StyledComponents";
import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import { ReactIconButton } from "app/components/React/ReactIconButton/ReactIconButton";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { i18nFilter } from "app/filters/i18nFilter";
import { itsMyOwnSegment } from "app/pages/segments/analysis/itsMyOwnSegment";
import { MAX_SEGMENTS_IN_GROUP } from "app/pages/segments/analysis/SegmentsAnalysisHelper";
import { MODE } from "app/pages/segments/analysis/SegmentsAnalysisTrafficContainerMode";
import { SegmentSelectorDropdown } from "app/pages/segments/modals/SegmentSelectorDropdown";
import { SegmentTypeBadge } from "pro-features/pages/segments/StyledComponents";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type {
    ICustomSegment,
    ICustomSegmentAccount,
    ICustomSegmentGroupWebsite,
    ICustomSegmentsGroup,
} from "app/services/segments/segmentsApiService";
import segmentsApiService, { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import type { ICustomSegmentMember } from "app/services/segments/SegmentsUtils";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { allTrackers } from "app/services/track/track";
import { IconContainer } from "app/UtilitiesAndConstants/UtilitiesComponents/IconContainer";

import {
    Domain,
    DomainWrapper,
    SegmentCategory,
    SegmentLabel,
    flexFixed,
    flexAutoScroll,
    flexAutoScrollContainer,
} from "./styledComponents";

const WARN_LARGE_GROUP_SEGMENTS = 10;

const proModalStyles: IProModalCustomStyles = {
    content: {
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        width: "600px",
        height: "600px",
        maxHeight: "calc(100% - 72px)",
    },
    overlay: { zIndex: 1070 }, // this was set for the "isBlacklisted" tooltip to be visible
};

interface ISegmentGroupModal {
    isOpen: boolean;
    segments: ICustomSegment[];
    allSegments: ICustomSegmentAccount[];
    segmentGroup?: ICustomSegmentsGroup;
    groupCreatedUpdated?: (group: ICustomSegmentsGroup) => any;
    toggleCreateUpdateSegmentGroupModal?: (isOpen: boolean, segmentGroup?: ICustomSegmentsGroup) => any;
    compareToSegment?: string;
}

const SegmentsCounterContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    font-size: 12px;
    line-height: 14px;
    color: ${colorsPalettes.carbon["300"]};
    padding: 8px 0;
`;

const SegmentCounter = styled.span<{ isErrorColor: boolean }>`
    color: ${({ isErrorColor }) => (isErrorColor ? colorsPalettes.red["400"] : colorsPalettes.carbon["300"])};
`;

const MarginRight = styled.span`
    margin-right: 4px;
`;

const SegmentItemContainer = styled.div`
    display: flex;
    padding: 4px 8px;
    font-size: 12px;
    line-height: 14px;
    ${StyledItemIcon} {
        align-self: center;
    }
    ${SegmentCategory} {
        text-indent: 10px;
        font-size: 12px;
    }
    ${Domain} {
        font-size: 14px;
    }
`;

const SegmentRowContainer = styled.div`
    display: flex;
    flex-grow: 1;
    align-items: center;
    ${SegmentItemContainer} {
        flex-grow: 1;
    }
`;

const ModalFooter = styled.div`
    display: flex;
    margin-top: 15px;
    button {
        margin-left: 8px;
    }
    ${flexFixed}
`;
const ModalFooterButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    flex-wrap: nowrap;
    margin-left: auto;
`;
const ModalFooterWarning = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    color: ${colorsPalettes.carbon["400"]};
    font-size: 12px;
    line-height: 14px;
    background: ${rgba(colorsPalettes.carbon["500"], 0.05)};
    padding: 4px 8px;
    border-radius: 4px;
`;
const ModalFooterWarningText = styled.div`
    margin-left: 0.5em;
`;
const ModalContent = styled.div`
    ${flexAutoScrollContainer}
    flex: auto;
`;
const SegmentListContainer = styled.div`
    border: 1px solid ${colorsPalettes.carbon["50"]};
    flex: auto;
    ${flexAutoScrollContainer}
`;
const SegmentSelectorContainer = styled.div`
    border-bottom: 1px solid ${colorsPalettes.carbon["50"]};
`;
const SegmentItemsContainer = styled.div`
    padding: 0 12px;
    ${flexAutoScroll}
    ${SegmentRowContainer}+${SegmentRowContainer} {
        border-top: 1px solid ${colorsPalettes.carbon["50"]};
    }
`;

const SegmentTypeBadgeHorizontal = styled(SegmentTypeBadge)`
    text-indent: 0;
    margin-left: 10px;
`;

const SegmentItem: React.FC<{ segment: ICustomSegment }> = ({ segment }) => (
    <SegmentItemContainer>
        <StyledItemIcon
            iconType={Type.Website}
            iconSrc={segment.favicon}
        />
        <SegmentLabel hasCategory={!!segment.segmentName}>
            <DomainWrapper>
                <Domain>{segment.domain}</Domain>
            </DomainWrapper>
            {segment.segmentName ? <SegmentCategory>{segment.segmentName}</SegmentCategory> : null}
        </SegmentLabel>
    </SegmentItemContainer>
);

const WebsiteItem: React.FC<{
    website: ICustomSegmentGroupWebsite;
    displayPill?: boolean;
}> = ({ website, displayPill = true }) => (
    <SegmentItemContainer>
        <StyledItemIcon
            iconType={Type.Website}
            iconSrc={website.favicon}
        />
        <SegmentLabel hasCategory={false}>
            <DomainWrapper>
                <Domain>{website.domain}</Domain>
                {displayPill ? <SegmentTypeBadgeHorizontal>WEBSITE</SegmentTypeBadgeHorizontal> : null}
            </DomainWrapper>
        </SegmentLabel>
    </SegmentItemContainer>
);

interface ISelectedSegmentContainer {
    segmentType: SEGMENT_TYPES;
    segment: ICustomSegmentMember;
    onRemoveSelected: (selectedSegment: ICustomSegmentMember, selectedSegmentType: SEGMENT_TYPES) => void;
    displayPill?: boolean;
}

export const SegmentItemRow: React.FC<ISelectedSegmentContainer> = ({
    segment,
    segmentType,
    onRemoveSelected,
    displayPill = true,
}) => {
    let segmentItemComponent;
    switch (segmentType) {
        case SEGMENT_TYPES.SEGMENT:
            segmentItemComponent = <SegmentItem segment={segment as ICustomSegment} />;
            break;
        case SEGMENT_TYPES.WEBSITE:
            segmentItemComponent = (
                <WebsiteItem
                    website={segment as ICustomSegmentGroupWebsite}
                    displayPill={displayPill}
                />
            );
            break;
    }
    const onRemoveProxy = React.useCallback(() => {
        onRemoveSelected(segment, segmentType);
    }, [segment, onRemoveSelected]);
    return (
        <SegmentRowContainer>
            {segmentItemComponent}
            <IconContainer size={20}>
                <ReactIconButton
                    iconSize="xs"
                    iconName="delete"
                    onClick={onRemoveProxy}
                />
            </IconContainer>
        </SegmentRowContainer>
    );
};

const SegmentGroupModal: React.FC<any> = (props: ISegmentGroupModal) => {
    const { segmentsService } = React.useMemo(
        () => ({
            segmentsService: new segmentsApiService(),
        }),
        [],
    );
    const {
        isOpen,
        segmentGroup,
        groupCreatedUpdated,
        toggleCreateUpdateSegmentGroupModal,
        compareToSegment,
        segments,
        allSegments,
    } = props;
    const createCallInProgress = React.useRef(false);
    const [groupName, setGroupName] = useState("");
    const [selectedSegments, setSelectedSegments] = useState<[ICustomSegmentMember, SEGMENT_TYPES][]>([]);
    const [focusGroupName, setFocusGroupName] = useState(false);
    const [isSmallList, setIsSmallList] = useState(false);
    const [isListDomainsOnly, setIsListDomainsOnly] = useState(false);
    const [isNameMissingError, setIsNameMissingError] = useState(false);
    const { state: currentRouteState, params } = useCurrentStateAndParams();
    const currentModule = proNavigator.getCurrentModule();
    const segmentGEOState = `${currentModule}-analysis-geography`;

    useEffect(() => {
        setIsSmallList(false);
        setIsListDomainsOnly(false);
    }, [selectedSegments]);

    useEffect(() => {
        setGroupName(segmentGroup?.name ?? "");
        const segmentsIds = segmentGroup ? segmentGroup.members : compareToSegment ? [compareToSegment] : [];
        const newSelectedSegments = SegmentsUtils.getSegmentObjectsByKeys(segmentsIds, {
            segments: allSegments,
            websites: segmentGroup?.websites,
        });
        newSelectedSegments.sort((a, b) =>
            SegmentsUtils.sortSegments.byDomainName(a[0] as ICustomSegment, b[0] as ICustomSegment),
        );
        setSelectedSegments(newSelectedSegments);
    }, [segmentGroup, compareToSegment]);

    const resetModal = () => {
        createCallInProgress.current = false;
        setGroupName(undefined);
        setSelectedSegments([]);
    };

    const onChangeGroupName = React.useCallback((event) => {
        setGroupName(event?.currentTarget?.value);
        setIsNameMissingError(false);
    }, []);

    const onClickClose = React.useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "segments/create/group/modal/cancel");
        toggleCreateUpdateSegmentGroupModal(false);
        resetModal();
    }, [toggleCreateUpdateSegmentGroupModal]);

    const hasSegmentsInGroup = (selectedSegments) => {
        if (selectedSegments.length === 0) {
            return false;
        }
        return selectedSegments.some((listItem) => {
            return listItem[1] === SEGMENT_TYPES.SEGMENT;
        });
    };

    const onSaveTracking = () => {
        const getWebsiteAmount = (): number => {
            return selectedSegments.filter((segment) => segment[1] === SEGMENT_TYPES.WEBSITE).length;
        };
        const getSegmentAmount = (): number => {
            return selectedSegments.filter((segment) => segment[1] === SEGMENT_TYPES.SEGMENT).length;
        };
        const domainsAmount = getWebsiteAmount();
        const segmentsAmount = getSegmentAmount();

        allTrackers.dispatchTrackEvent("Button", "click", "segments/create/group/modal/success", {
            domains_amount: domainsAmount,
            segments_amount: segmentsAmount,
            entities_amount: domainsAmount + segmentsAmount,
        });
    };

    const createSegmentsGroup = React.useCallback(() => {
        if (createCallInProgress.current) {
            return;
        }
        if (groupName === "") {
            if (selectedSegments.length < 2) {
                setFocusGroupName(true);
                setIsNameMissingError(true);
                setIsSmallList(true);
                return;
            }
            setFocusGroupName(true);
            setIsNameMissingError(true);
            return;
        }
        if (selectedSegments.length < 2) {
            setIsSmallList(true);
            return;
        }
        if (!hasSegmentsInGroup(selectedSegments)) {
            setIsListDomainsOnly(true);
            return;
        }
        createCallInProgress.current = true;

        onSaveTracking();

        const { segments: newSegments, websites: newWebsites } = selectedSegments.reduce(
            (acc, selectedSegment) => {
                const [selectedSegmentObj, selectedSegmentType] = selectedSegment;
                switch (selectedSegmentType) {
                    case SEGMENT_TYPES.SEGMENT:
                        acc.segments.push(selectedSegmentObj.id);
                        break;
                    case SEGMENT_TYPES.WEBSITE:
                        acc.websites.push(selectedSegmentObj.domain);
                        break;
                }
                return acc;
            },
            { segments: [], websites: [] },
        );
        const createUpdateGroupPromise = segmentGroup
            ? segmentsService.updateCustomSegmentsGroup({
                  name: groupName,
                  id: segmentGroup.id,
                  segments: newSegments,
                  websites: newWebsites,
              })
            : segmentsService.createCustomSegmentsGroup({
                  name: groupName,
                  segments: newSegments,
                  websites: newWebsites,
              });
        createUpdateGroupPromise
            .then((newGroup) => {
                // Resolve what is the current module, so that we could build the relevant segments
                // state name according to the module. this is done to support Solutions 2.0: segments analysis
                // has multiple routes in the platform.
                const segmentAnalysisState = `${currentModule}-analysis-traffic`;
                const segmentMMXState = `${currentModule}-analysis-marketingChannels`;
                const segmentGEOState = `${currentModule}-analysis-geography`;
                const segmentStatesArray = [segmentAnalysisState, segmentMMXState, segmentGEOState];

                groupCreatedUpdated(newGroup);
                toggleCreateUpdateSegmentGroupModal(false);
                resetModal();
                if (
                    segmentStatesArray.includes(currentRouteState.name) &&
                    params?.mode === MODE.group &&
                    params?.id === newGroup.id
                ) {
                    // if editing the current group in view
                    proNavigator.go(currentRouteState, params, { reload: true });
                } else {
                    const targetState = segmentStatesArray.includes(currentRouteState.name)
                        ? currentRouteState.name
                        : segmentAnalysisState;
                    proNavigator.go(targetState, {
                        id: newGroup.id,
                        mode: MODE.group,
                        ...SegmentsUtils.getPageFilterParams(newGroup.id),
                    }); // otherwise, go to the new group
                }
            })
            .catch((err) => {
                Sentry.error("Failed to create segment group", err);
                createCallInProgress.current = false;
            });
    }, [segmentGroup, selectedSegments, groupName, groupCreatedUpdated, toggleCreateUpdateSegmentGroupModal]);

    const onSegmentSelection = React.useCallback(
        (selectedSegment: ICustomSegmentMember, selectedSegmentType: SEGMENT_TYPES) => {
            let matchSelectedAttr = "id";
            if (selectedSegmentType === SEGMENT_TYPES.WEBSITE) {
                matchSelectedAttr = "domain";
            }
            // avoid of adding existing members
            if (
                selectedSegments.findIndex(
                    ([memberObj, memberType]) =>
                        memberType === selectedSegmentType &&
                        memberObj[matchSelectedAttr] === selectedSegment[matchSelectedAttr],
                ) === -1
            ) {
                setSelectedSegments([[selectedSegment, selectedSegmentType], ...selectedSegments]);
            }
        },
        [selectedSegments],
    );

    const onRemoveSelection = React.useCallback(
        (selectedSegment: ICustomSegmentMember, selectedSegmentType: SEGMENT_TYPES) => {
            setSelectedSegments(
                selectedSegments.filter(
                    ([segment, segmentType]) => segment !== selectedSegment || segmentType !== selectedSegmentType,
                ),
            );
        },
        [selectedSegments],
    );

    const [mySegmentsSorted, accountSegmentsSorted] = React.useMemo(
        () => [
            (segments?.slice() ?? []).sort(SegmentsUtils.sortSegments.byCreationDomain),
            (allSegments?.filter((segment) => !itsMyOwnSegment(segment.userId)) ?? []).sort(
                SegmentsUtils.sortSegments.byCreationDomain,
            ),
        ],
        [segments, allSegments],
    );
    const [filteredMySegments, filteredAccountSegments] = React.useMemo(() => {
        const filteredMySegments = mySegmentsSorted?.filter(
            (segment) => selectedSegments.findIndex(([selectedSeg]) => selectedSeg.id === segment.id) === -1,
        );
        const filteredAccountSegments = accountSegmentsSorted?.filter(
            (segment) => selectedSegments.findIndex(([selectedSeg]) => selectedSeg.id === segment.id) === -1,
        );
        return [filteredMySegments, filteredAccountSegments];
    }, [selectedSegments, mySegmentsSorted, accountSegmentsSorted]);

    const segmentsCounterDataObj = React.useMemo(
        () => ({
            count: selectedSegments.length,
            max: MAX_SEGMENTS_IN_GROUP,
        }),
        [selectedSegments.length],
    );

    return (
        <ProModal
            isOpen={isOpen}
            onCloseClick={onClickClose}
            customStyles={proModalStyles}
        >
            <ModalContent>
                <AutosizeInput
                    isUnderline={true}
                    onChange={onChangeGroupName}
                    value={groupName ?? ""}
                    autoFocus={!segmentGroup}
                    placeholder={i18nFilter()("segments.group.modal.group.name.placeholder")}
                    maxLength={70}
                    error={isNameMissingError}
                    isFocused={focusGroupName}
                />
                <SegmentsCounterContainer>
                    <I18n>segments.group.modal.list.text</I18n>
                    <SegmentCounter isErrorColor={isSmallList || isListDomainsOnly}>
                        {!isSmallList && isListDomainsOnly ? (
                            <MarginRight>
                                <I18n>"segments.group.modal.domains.only.error.message"</I18n>
                            </MarginRight>
                        ) : null}
                        <I18n dataObj={segmentsCounterDataObj}>
                            {isSmallList
                                ? "segments.group.modal.counter.segments.error.message"
                                : "segments.group.modal.counter.segments"}
                        </I18n>
                    </SegmentCounter>
                </SegmentsCounterContainer>
                <SegmentListContainer>
                    <SegmentSelectorContainer>
                        <SegmentSelectorDropdown
                            onSegmentSelection={onSegmentSelection}
                            segments={filteredMySegments}
                            accountSegments={filteredAccountSegments}
                            selectedSegments={selectedSegments}
                            disabled={selectedSegments.length >= MAX_SEGMENTS_IN_GROUP}
                        />
                    </SegmentSelectorContainer>
                    <SegmentItemsContainer>
                        {selectedSegments.map(([segment, segmentType]) => (
                            <SegmentItemRow
                                key={segment.id}
                                segmentType={segmentType}
                                segment={segment}
                                onRemoveSelected={onRemoveSelection}
                            />
                        ))}
                    </SegmentItemsContainer>
                </SegmentListContainer>
            </ModalContent>
            <ModalFooter>
                {selectedSegments.length > WARN_LARGE_GROUP_SEGMENTS && currentRouteState.name !== segmentGEOState ? (
                    <ModalFooterWarning>
                        <Badge type="beta" />
                        <ModalFooterWarningText>
                            <I18n>segments.group.modal.warning.large</I18n>
                        </ModalFooterWarningText>
                    </ModalFooterWarning>
                ) : null}
                <ModalFooterButtons>
                    <Button
                        type="flat"
                        onClick={onClickClose}
                        dataAutomation="segments-group-modal-cancel-button"
                    >
                        <ButtonLabel>
                            <I18n>segments.group.modal.cancel.button</I18n>
                        </ButtonLabel>
                    </Button>
                    <Button
                        type="primary"
                        isDisabled={false}
                        onClick={createSegmentsGroup}
                        dataAutomation="segments-group-modal-save-button"
                    >
                        <ButtonLabel>
                            <I18n>segments.group.modal.save.button</I18n>
                        </ButtonLabel>
                    </Button>
                </ModalFooterButtons>
            </ModalFooter>
        </ProModal>
    );
};

function mapStateToProps(store) {
    const {
        segmentsModule: {
            customSegmentsMeta,
            createUpdateGroupSegmentModalIsOpen: isOpen,
            segmentGroup,
            compareToSegment,
        },
    } = store;
    return {
        isOpen,
        ...(isOpen && {
            segments: customSegmentsMeta?.Segments,
            groups: customSegmentsMeta?.SegmentGroups,
            allSegments: customSegmentsMeta?.AccountSegments,
            segmentGroup,
            compareToSegment,
        }),
    };
}

function mapDispatchToProps(dispatch) {
    return {
        toggleCreateUpdateSegmentGroupModal: (isOpen) => {
            dispatch(toggleCreateUpdateSegmentGroupModal(isOpen));
        },
        groupCreatedUpdated: (group) => {
            dispatch(groupCreatedUpdated(group));
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(SegmentGroupModal);
